import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { InterviewMethodSchema } from '../interview/interview';

/**
 * A window as **the member** may pick it (D9a).
 *
 * Narrower than the staff shape on purpose: no audience (it is theirs by
 * construction), no status (only `open` reaches here), no bookings (other
 * members' appointments are not theirs to see), no location (it is told after
 * booking, on the interview — a link published to a browser is a link
 * published to a stranger). `remainingCapacity` is advisory — the claim is the
 * server's, under the slot's lock — but it lets the picker say "one place left".
 */
export const MemberInterviewSlotSchema = z
  .object({
    id: UuidSchema,
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime(),
    method: InterviewMethodSchema,
    remainingCapacity: z.number().int().positive(),
  })
  .strict();

export type MemberInterviewSlot = z.infer<typeof MemberInterviewSlotSchema>;

/**
 * The member's availability read, with the two bounds the calendar draws from.
 *
 * `horizonDays` and `leadMinutes` come back **with** the slots so the picker can
 * grey out what the server would refuse without a second request — a control
 * must filter by everything the write refuses (P25e1).
 */
export const MemberInterviewAvailabilitySchema = z
  .object({
    slots: z.array(MemberInterviewSlotSchema),
    /** How far ahead of now a slot may sit — `verification.interviewSlotHorizonDays`. */
    horizonDays: z.number().int().positive(),
    /** The least notice a booking needs — `verification.interviewBookingLeadMinutes`. */
    leadMinutes: z.number().int().nonnegative(),
  })
  .strict();

export type MemberInterviewAvailability = z.infer<typeof MemberInterviewAvailabilitySchema>;
